import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Loader2, ImageOff } from 'lucide-react';
import { useCart } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import { AfterpayBadge } from '../components/AfterpayBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartPage = () => {
  const { cart, updateCartItem, removeFromCart, clearCart, loading } = useCart();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  const cartTotal = cart.items?.reduce((total, item) => {
    return total + ((item.price || 0) * (item.quantity || 0));
  }, 0) || 0;

  const cartOriginalTotal = cart.items?.reduce((total, item) => {
    const basePrice = item.original_price || item.price || 0;
    return total + (basePrice * (item.quantity || 0));
  }, 0) || 0;

  const cartSavings = Math.max(0, cartOriginalTotal - cartTotal);

  const updateQuantity = (productId, quantity) => {
    if (quantity < 1) {
      removeFromCart(productId);
    } else {
      updateCartItem(productId, quantity);
    }
  };

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    setCheckoutError('');
    try {
      const items = cart.items.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
      }));
      const response = await axios.post(`${API}/checkout/create`, { items });
      const { checkout_url, unavailable_items } = response.data;

      if (unavailable_items && unavailable_items.length > 0) {
        setCheckoutError(`Note: ${unavailable_items.join(', ')} not yet available on Shopify and won't be included in checkout.`);
      }

      if (checkout_url) {
        window.location.href = checkout_url;
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to create checkout. Please try again.';
      setCheckoutError(detail);
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] pt-32 flex items-center justify-center">
        <div className="w-16 h-16 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!cart || cart.items?.length === 0) {
    return (
      <div className="min-h-screen bg-[#050505] pt-32" data-testid="cart-page-empty">
        <div className="max-w-4xl mx-auto px-6 text-center py-24">
          <div className="w-24 h-24 mx-auto mb-8 border border-[#D4AF37]/30 flex items-center justify-center">
            <ShoppingBag size={40} className="text-[#D4AF37]/50" />
          </div>
          <h1 className="font-heading text-4xl text-white mb-4">YOUR BAG IS EMPTY</h1>
          <p className="font-body text-white/50 mb-8">Discover our exquisite collection of fragrances</p>
          <Link to="/products">
            <button className="bg-[#D4AF37] text-[#050505] px-10 py-4 font-heading text-xs tracking-[0.2em] hover:bg-[#E5C158] transition-colors" data-testid="explore-collection-btn">
              EXPLORE COLLECTION
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24" data-testid="cart-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <div className="flex justify-between items-end mb-12">
          <div>
            <p className="font-arabic text-2xl text-[#D4AF37]/60 mb-2">حقيبتك</p>
            <h1 className="font-heading text-4xl tracking-wide text-white">
              YOUR <span className="text-[#D4AF37]">BAG</span>
            </h1>
          </div>
          <button onClick={clearCart} className="text-white/50 hover:text-[#D4AF37] font-heading text-xs tracking-[0.2em] transition-colors" data-testid="clear-cart-button">
            CLEAR ALL
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          <div className="lg:col-span-2 space-y-6 min-w-0">
            {cart.items?.map((item) => (
              <div key={item.product_id} className="flex gap-4 md:gap-6 p-4 md:p-6 border border-[#D4AF37]/20 bg-[#0a0a0a]" data-testid={`cart-item-${item.product_id}`}>
                <div className="w-20 h-28 md:w-32 md:h-40 flex-shrink-0 overflow-hidden border border-[#D4AF37]/10">
                  {item.image ? (
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-[#0d0d0d]">
                      <ImageOff size={24} className="text-[#D4AF37]/20" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <h3 className="font-heading text-base md:text-lg text-white truncate">{item.name}</h3>
                      <p className="font-body text-white/50 text-xs md:text-sm">{item.subtitle} {item.size && `\u00B7 ${item.size}`}</p>
                    </div>
                    <button onClick={() => removeFromCart(item.product_id)} className="text-white/30 hover:text-red-400 transition-colors flex-shrink-0 self-start" data-testid={`remove-item-${item.product_id}`}>
                      <Trash2 size={18} />
                    </button>
                  </div>
                  <div className="flex flex-wrap justify-between items-end gap-x-4 gap-y-3 mt-3 md:mt-4">
                    <div className="flex items-center border border-[#D4AF37]/30">
                      <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="p-2 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors">
                        <Minus size={14} />
                      </button>
                      <span className="px-3 md:px-4 font-heading text-white text-sm">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} className="p-2 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors">
                        <Plus size={14} />
                      </button>
                    </div>
                    <div className="flex flex-col items-end gap-1" data-testid={`cart-item-pricing-${item.product_id}`}>
                      {item.on_sale && item.original_price ? (
                        <>
                          <div className="flex items-center gap-2 flex-wrap justify-end">
                            <span className="bg-[#D4AF37] text-[#050505] font-heading text-[10px] tracking-[0.15em] px-2 py-0.5 whitespace-nowrap" data-testid={`cart-item-discount-badge-${item.product_id}`}>
                              {`-${item.discount_pct}% OFF`}
                            </span>
                            <span className="font-body text-white/40 text-sm line-through" data-testid={`cart-item-original-${item.product_id}`}>
                              A${(item.original_price * item.quantity).toFixed(2)}
                            </span>
                          </div>
                          <p className="font-heading text-lg md:text-xl text-[#D4AF37]" data-testid={`cart-item-sale-${item.product_id}`}>
                            A${(item.price * item.quantity).toFixed(2)}
                          </p>
                        </>
                      ) : (
                        <p className="font-heading text-lg md:text-xl text-[#D4AF37]" data-testid={`cart-item-price-${item.product_id}`}>
                          A${(item.price * item.quantity).toFixed(2)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="min-w-0">
            <div className="border border-[#D4AF37]/20 bg-[#0a0a0a] p-6 md:p-8 lg:sticky lg:top-32">
              <h2 className="font-heading text-lg tracking-wide text-white mb-6">ORDER SUMMARY</h2>
              <div className="space-y-4 pb-6 border-b border-[#D4AF37]/20">
                <div className="flex justify-between">
                  <span className="font-body text-white/50">Subtotal</span>
                  <span className="font-heading text-white">A${cartOriginalTotal.toFixed(2)}</span>
                </div>
                {cartSavings > 0 && (
                  <div className="flex justify-between" data-testid="cart-savings-row">
                    <span className="font-body text-[#D4AF37]/80">Father's Day Savings</span>
                    <span className="font-heading text-[#D4AF37]" data-testid="cart-savings-amount">
                      -A${cartSavings.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="font-body text-white/50">Shipping</span>
                  <span className="font-heading text-white/40 text-sm">Calculated at checkout</span>
                </div>
              </div>
              <div className="flex justify-between pt-6 pb-3">
                <span className="font-heading text-lg text-white">TOTAL</span>
                <span className="font-heading text-2xl text-[#D4AF37]" data-testid="cart-total">
                  A${cartTotal.toFixed(2)}
                </span>
              </div>

              <AfterpayBadge amount={cartTotal} className="pb-6" testId="cart-afterpay" />

              {checkoutError && (
                <div className="mb-4 p-3 border border-red-400/30 bg-red-400/5 text-red-300 font-body text-xs" data-testid="checkout-error">
                  {checkoutError}
                </div>
              )}

              <button
                onClick={handleCheckout}
                disabled={checkoutLoading}
                className="w-full bg-[#D4AF37] text-[#050505] py-5 font-heading text-sm tracking-[0.2em] hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-3 disabled:opacity-60"
                data-testid="checkout-button"
              >
                {checkoutLoading ? (
                  <><Loader2 size={18} className="animate-spin" /> PROCESSING...</>
                ) : (
                  <>CHECKOUT <ArrowRight size={18} /></>
                )}
              </button>

              <p className="text-center mt-3 font-body text-white/30 text-[10px]">
                Secure checkout powered by Shopify
              </p>

              <Link to="/products" className="block mt-4">
                <button className="w-full border border-[#D4AF37]/30 text-[#D4AF37] py-4 font-heading text-xs tracking-[0.2em] hover:bg-[#D4AF37]/10 transition-colors">
                  CONTINUE SHOPPING
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
